import { ExternalLink, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ShortlistActionButton } from "@/components/agent-shortlist/ShortlistActionButton";

const toneStyles = {
  bullish: "border-emerald-200 bg-emerald-50 text-emerald-800",
  balanced: "border-[#D4AF37]/25 bg-[#fff8ea] text-[#6b5c2d]",
  caution: "border-[#e8d8c1] bg-[#fffaf1] text-[#7a5a3d]",
};

export const ListingReasoningPanel = ({ buildShortlistPath, forecast, isAgentTheme = false, listing, onCopyListingLink, showShortlistActions = true }) => {
  if (!forecast || !listing) return null;

  const eyebrow = isAgentTheme ? "Why it stands out" : "Shortlist intelligence";
  const heading = isAgentTheme
    ? "A clearer read on whether this home deserves a closer look."
    : "Smarter reasoning for whether this listing deserves a closer look.";
  const openShortlistLabel = isAgentTheme ? "Open shortlist" : "Open shortlist studio";
  const nextMoveLabel = isAgentTheme ? "Helpful next move" : "Recommended next move";

  return (
    <section className="signature-greige-shell rounded-[30px] p-6 shadow-[0_22px_70px_rgba(0,0,0,0.06)] md:p-8" data-testid="listing-detail-reasoning-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B6E3D]">{eyebrow}</p>
          <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#2C2C2C]" data-testid="listing-detail-reasoning-heading">
            {heading}
          </h2>
          <p className="mt-4 text-sm leading-8 text-[#5D584F]" data-testid="listing-detail-reasoning-summary">{forecast.district_signal_summary || forecast.summary}</p>
        </div>

        {showShortlistActions ? (
          <div className="flex flex-wrap gap-2">
            <ShortlistActionButton listing={listing} testId="listing-detail-shortlist-button" variant="soft" />
            <button className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid="listing-detail-copy-link-button" onClick={onCopyListingLink} type="button">
              <Share2 className="mr-2 h-4 w-4" /> Copy listing link
            </button>
            <Link className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid="listing-detail-open-shortlist-button" to={buildShortlistPath}>
              <ExternalLink className="mr-2 h-4 w-4" /> {openShortlistLabel}
            </Link>
          </div>
        ) : null}
      </div>

      <div className="mt-6 rounded-[24px] border border-[#D4AF37]/25 bg-white/80 px-5 py-4" data-testid="listing-detail-shortlist-strategy-card">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">{nextMoveLabel}</p>
        <p className="mt-3 text-sm leading-8 text-[#5D584F]" data-testid="listing-detail-shortlist-strategy-note">{forecast.shortlist_strategy_note || forecast.investment_verdict_text}</p>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3" data-testid="listing-detail-reasoning-cards-grid">
        {(forecast.reasoning_cards || []).map((card, index) => (
          <div className={`rounded-[24px] border p-5 ${toneStyles[card.tone] || toneStyles.balanced}`} data-testid={`listing-detail-reasoning-card-${index + 1}`} key={`${card.title}-${index}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em]" data-testid={`listing-detail-reasoning-card-title-${index + 1}`}>{card.title}</p>
            <p className="mt-4 text-2xl font-semibold" data-testid={`listing-detail-reasoning-card-metric-${index + 1}`}>{card.metric_value}</p>
            <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.16em] opacity-70" data-testid={`listing-detail-reasoning-card-label-${index + 1}`}>{card.metric_label}</p>
            <p className="mt-4 text-sm leading-7" data-testid={`listing-detail-reasoning-card-summary-${index + 1}`}>{card.summary}</p>
          </div>
        ))}
      </div>
    </section>
  );
};